import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import IntroStep from '../OnboardingWizard/components/IntroStep/IntroStep';

import StepperContent from './components/StepperContent/StepperContent';
import StepperSidebar from './components/StepperSidebar/StepperSidebar';
import { useFooterConfig } from './hooks/useFooterConfig';
import { useIntroTransition } from './hooks/useIntroTransition';
import { useStepperOrchestration } from './hooks/useStepperOrchestration';
import { useStepperScroll } from './hooks/useStepperScroll';
import { useWindowWidth } from './hooks/useWindowWidth';
import { INTRO_STEP_DEFAULT_DEFINITION, NARROW_SCREEN_BREAKPOINT } from './lib/constants';

import './lib/Stepper.scss';

const EMPTY_SET = new Set();

const EmptyFallbackStep = () => null;

/**
 * @param {import('./lib/propTypes').StepperProps} props
 */
const Stepper = ({
  wizardState = {},
  practice = {},
  isAdmin = false,
  wizardKind,
  hiddenSubstepCodes = EMPTY_SET,
  componentStepsConfig = {},
  fallbackStepComponent: FallbackStepComponent = EmptyFallbackStep,
  delegations = null,
  navigateToCode = null,
  onNavigateToCodeHandled
} = {}) => {
  const [headerButtonProps, setHeaderButtonProps] = useState(null);
  const [headerInfoTooltip, setHeaderInfoTooltip] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainContentRef = useRef(null);
  const { isNarrowScreen } = useWindowWidth(NARROW_SCREEN_BREAKPOINT);

  const {
    instance,
    phases,
    stepsState,
    activeSubstep,
    expandStep,
    collapseStep,
    navigateToSubstep,
    goNext,
    goPrev,
    activateStepWithHiddenCheck,
    orchestratorData,
    progressValue
  } = useStepperOrchestration({
    wizardState,
    wizardKind,
    hiddenSubstepCodes,
    navigateToCode,
    onNavigateToCodeHandled
  });

  const { isEnteringFromIntro, handleIntroNext } = useIntroTransition(goNext);
  const { footerConfig, setFooterConfig, updateConfigs, addFooterActions } =
    useFooterConfig(goPrev);
  const { shouldAnimateHeader } = useStepperScroll(mainContentRef, activeSubstep?.code);

  // Close sidebar when switching to wide screen
  useEffect(() => {
    if (!isNarrowScreen) setSidebarOpen(false);
  }, [isNarrowScreen]);

  const closeSidebar = useCallback(() => {
    if (isNarrowScreen) setSidebarOpen(false);
  }, [isNarrowScreen]);

  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);

  // Reset info tooltip when navigating to a different substep
  useEffect(() => {
    setHeaderInfoTooltip(null);
  }, [activeSubstep?.code]);

  const ActiveComponent =
    componentStepsConfig[activeSubstep?.code]?.component || FallbackStepComponent;
  const componentStepConfig = componentStepsConfig[activeSubstep?.code] || {};
  const headerOptions = {
    title: activeSubstep?.name,
    description: activeSubstep?.description
  };
  const smartSuggestions = componentStepConfig.smartSuggestions || [];

  const isIntroStep = activeSubstep?.code === INTRO_STEP_DEFAULT_DEFINITION.substeps[0].code;

  if (isIntroStep) {
    return (
      <div className="flex h-screen w-screen">
        <IntroStep onNext={handleIntroNext} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'h-screen bg-gradient-to-b from-primary-50 to-primary-200 px-10 py-8 [@media(max-width:1200px)]:px-6 [@media(max-width:1200px)]:py-8'
      )}>
      <div
        className={cn(
          'relative flex h-full justify-center gap-10 [@media(max-width:1200px)]:gap-6'
        )}>
        <StepperSidebar
          stepsState={stepsState}
          phases={phases}
          hiddenSubstepCodes={hiddenSubstepCodes}
          isNarrowScreen={isNarrowScreen}
          sidebarOpen={sidebarOpen}
          closeSidebar={closeSidebar}
          isEnteringFromIntro={isEnteringFromIntro}
          smartSuggestions={smartSuggestions}
          isAdmin={isAdmin}
          practiceId={practice?.id}
          onExpandStep={expandStep}
          onCollapseStep={collapseStep}
          onActivateStep={activateStepWithHiddenCheck}
          onNavigateSubstep={navigateToSubstep}
        />

        <div
          className={cn(
            'stepper-main-content relative w-full min-w-0 max-w-[1200px] flex-1 justify-center overflow-hidden rounded-xl bg-white',
            isEnteringFromIntro && 'entering'
          )}
          onClick={closeSidebar}>
          <StepperContent
            isAdmin={isAdmin}
            isNarrowScreen={isNarrowScreen}
            isEnteringFromIntro={isEnteringFromIntro}
            mainContentRef={mainContentRef}
            wizardState={wizardState}
            practice={practice}
            headerProps={{
              headerButtonProps,
              headerOptions,
              headerInfoTooltip,
              setHeaderButtonProps,
              setHeaderInfoTooltip,
              progressValue,
              shouldAnimateHeader,
              onToggleSidebar: toggleSidebar
            }}
            footerProps={{
              footerConfig,
              setFooterConfig,
              updateConfigs,
              addFooterActions,
              delegations
            }}
            stepProps={{
              ActiveComponent,
              activeSubstep,
              instance,
              stepsState,
              componentStepConfig,
              orchestratorData,
              navigateToSubstep,
              goNext,
              goPrev
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Stepper;
